import { format } from 'date-fns';

import type {
  MatchPreviewData,
  MatchScheduleMarkets,
  MatchPreviewTeam,
  MatchPreviewTeamMarkets,
  MatchPreviewTeamMarketValue,
  MatchScheduleMarketStatus
} from 'app/components/MatchPreview/types';
import type { SectionListData, SectionListHeader } from 'app/components/SectionList';
import { formatHandicapOutcome, getMostBalancedMarket } from 'app/utils/markets';
import { getAllVideogamesQueryList, isCompetitorVideogame } from 'app/utils/videogame';
import type {
  EventTreeValue,
  PandapiMatchWithCompetitorsWithStatus
} from 'app/pages/EventsPage/types';

function setMarketsInOpponent(
  opponent: PandapiTeam | PandapiPlayer,
  scheduleMarkets: MatchScheduleMarkets,
  index: number
): MatchPreviewTeam {
  const marketsArray = Object.entries(scheduleMarkets) as [
    MatchPreviewDisplayedMarket,
    Market | undefined
  ][];
  const markets = marketsArray.reduce((formattedMarkets, [marketKey, market]) => {
    if (market) {
      const outcome =
        marketKey === 'map-over-under'
          ? market.selections[index]
          : market.selections.find(({ participant_id }) => participant_id === opponent.id);
      let teamMarket = {} as MatchPreviewTeamMarketValue;
      if (outcome && outcome.probability_with_overround !== null) {
        teamMarket = {
          proba: outcome.probability_with_overround
        };

        if (marketKey === 'map-handicap') {
          teamMarket = {
            ...teamMarket,
            indicator: formatHandicapOutcome('', { ...outcome, name: '' }, market).trim()
          };
        }

        if (marketKey === 'map-over-under') {
          teamMarket = {
            ...teamMarket,
            line: market.line ?? undefined,
            indicator: outcome.name?.slice(0, 1)
          };
        }

        formattedMarkets[marketKey] = teamMarket;
      }
    }
    return formattedMarkets;
  }, {} as MatchPreviewTeamMarkets);

  return {
    ...opponent,
    markets
  };
}

function getTieProba(market: Market): number | undefined {
  const tieOutcome = market.selections.find(outcome => outcome.participant_id === null);
  if (tieOutcome && tieOutcome.probability_with_overround !== null) {
    return tieOutcome.probability_with_overround;
  }
  return undefined;
}

const getScheduleMarketStatus = (
  scheduleMarkets: MatchScheduleMarkets
): MatchScheduleMarketStatus => {
  const marketsArray = Object.entries(scheduleMarkets) as [
    MatchPreviewDisplayedMarket,
    Market | undefined
  ][];
  return marketsArray.reduce((marketStatus, [marketKey, market]) => {
    marketStatus[marketKey] = market?.status;
    return marketStatus;
  }, {} as MatchScheduleMarketStatus);
};

const getCorrectMarketToDisplay = (marketsFromTemplates: Market[]): Market | undefined => {
  if (marketsFromTemplates.length > 1) {
    if (isCompetitorVideogame(marketsFromTemplates[0].videogame_slug)) {
      // Only for ebattlers
      const market = marketsFromTemplates.find(market => market.template === 'winner-3-way');
      if (market) {
        return market;
      }
    }
    return getMostBalancedMarket(marketsFromTemplates) ?? undefined;
  }

  return marketsFromTemplates[0] ?? undefined;
};

const getScheduleMarkets = (
  match: PandapiMatchWithCompetitorsWithStatus,
  scheduleMetaData: ScheduleMetaData
): MatchScheduleMarkets => {
  const TEMPLATES_FROM_MARKET: Record<MatchPreviewDisplayedMarket, string[]> = {
    winner: ['winner-2-way', 'winner-3-way'],
    'map-handicap': ['map-handicap-1', 'map-handicap-2', 'map-handicap-3', 'map-handicap-4'],
    'map-over-under': ['map-over-under']
  };
  const TEMPLATE_FROM_MARKET_ARRAY = Object.entries(TEMPLATES_FROM_MARKET) as [
    MatchPreviewDisplayedMarket,
    string[]
  ][];

  return TEMPLATE_FROM_MARKET_ARRAY.reduce((markets, [marketKey, templates]) => {
    const marketsFromTemplates =
      scheduleMetaData.markets.filter(
        ({ event_id, template }) => event_id === match.id && templates.includes(template)
      ) ?? undefined;

    const market = getCorrectMarketToDisplay(marketsFromTemplates);

    markets[marketKey] = market;
    return markets;
  }, {} as MatchScheduleMarkets);
};

function mergeMatchData(
  match: PandapiMatchWithCompetitorsWithStatus,
  scheduleMetaData: ScheduleMetaData,
  incidents: MatchIncident[],
  traderInputs: TraderInputs[],
  blacklistedMatches: BlacklistMatch[]
): MatchPreviewData {
  const matchSettlementCounter = scheduleMetaData.reviewed_counters.find(
    counter => counter.match_id === match.id
  );
  const matchIncidents = incidents.filter(({ match_id }) => match_id === match.id);

  const matchTraderInputs = traderInputs.find(({ match_id }) => match_id === match.id);

  const matchBlacklist = blacklistedMatches.find(({ id }) => id === match.id)?.suspicious_players;

  const scheduleMarkets = getScheduleMarkets(match, scheduleMetaData);

  const winnerMarket = scheduleMarkets.winner;
  const winner_market_template = winnerMarket ? winnerMarket.template : undefined;
  const tie_proba = winnerMarket ? getTieProba(winnerMarket) : undefined;

  const markets_status = getScheduleMarketStatus(scheduleMarkets);

  const opponents = match.opponents.map(({ opponent }, index) =>
    setMarketsInOpponent(opponent, scheduleMarkets, index)
  );

  return {
    ...match,
    opponents,
    incidents: matchIncidents,
    traderInputs: matchTraderInputs,
    review_status: matchSettlementCounter,
    winner_market: {
      tie_proba,
      winner_market_template
    },
    markets_status,
    blacklistedPlayers: matchBlacklist
  };
}

function getTournamentsFilter(
  value: EventTreeValue,
  displayExternalProvidersMatches: boolean
): { [key: string]: string } {
  const videogamesTournaments = value.videogames.map(videogame => ({
    slug: videogame.slug,
    selected: videogame.selected,
    tournaments: videogame.series.map(serie => serie.tournaments).flat()
  }));

  // filter by videogame when all videogames are selected or all tournaments of all videogames are selected
  const onlyVideogameFilter = videogamesTournaments.every(
    v => v.tournaments.every(s => s.selected) || v.tournaments.every(s => !s.selected)
  );

  const tournamentIds = onlyVideogameFilter
    ? ''
    : videogamesTournaments
        .reduce((acc, videogame) => {
          const selectedTournamentsIds =
            videogame.selected && !videogame.tournaments.some(s => s.selected)
              ? videogame.tournaments.map(s => s.id)
              : videogame.tournaments.filter(s => s.selected).map(s => s.id);

          return [...acc, ...selectedTournamentsIds];
        }, [] as number[])
        .join(',');

  const filters: { [key: string]: string } = {};

  if (onlyVideogameFilter) {
    const selectedVideogames = videogamesTournaments.filter(
      v => v.selected || (v.tournaments.length > 0 && v.tournaments.every(s => s.selected))
    );
    const videogameSlugs = selectedVideogames.length
      ? selectedVideogames.map(v => v.slug).join(',')
      : getAllVideogamesQueryList(
          displayExternalProvidersMatches,
          value.videogames.map(v => v.slug)
        );

    if (videogameSlugs) {
      filters['filter[videogame]'] = videogameSlugs;
    }
  }

  if (tournamentIds) {
    filters['filter[tournament_id]'] = tournamentIds;
  }

  return filters;
}

function getFormatedMatchesForList(matches: PandapiMatchWithCompetitorsWithStatus[]) {
  return matches.reduce(
    (
      acc: SectionListData<PandapiMatchWithCompetitorsWithStatus>,
      curr: PandapiMatchWithCompetitorsWithStatus
    ) => {
      const date = curr.scheduled_at ? getMatchDate(curr.scheduled_at) : undefined;
      const dateIndexInList = acc.findIndex(item => item.header.key === date);

      if (!date) {
        return acc;
      }

      if (dateIndexInList === -1) {
        return [
          ...acc,
          {
            header: {
              key: date,
              label: date,
              value: curr.scheduled_at ? format(new Date(curr.scheduled_at), 'MMMM dd') : ''
            } as SectionListHeader,
            data: [{ id: curr.id, value: curr }]
          }
        ];
      }

      if (dateIndexInList !== -1) {
        acc[dateIndexInList].data.push({ id: curr.id, value: curr });
      }

      return acc;
    },
    []
  );
}

function getMatchDate(beginAt: string): string {
  const matchDate = format(new Date(beginAt), 'MM/dd/yyyy');
  const todayDate = new Date();

  const yesterdayDate = new Date();
  yesterdayDate.setDate(yesterdayDate.getDate() - 1);

  const tomorrowDate = new Date();
  tomorrowDate.setDate(tomorrowDate.getDate() + 1);

  const today = format(todayDate, 'MM/dd/yyyy');
  const yesterday = format(yesterdayDate, 'MM/dd/yyyy');
  const tomorrow = format(tomorrowDate, 'MM/dd/yyyy');

  if (matchDate === today) {
    return 'Today';
  }
  if (matchDate === yesterday) {
    return 'Yesterday';
  }
  if (matchDate === tomorrow) {
    return 'Tomorrow';
  }

  return format(new Date(beginAt), 'MMMM dd');
}

export { mergeMatchData, getTournamentsFilter, getFormatedMatchesForList, setMarketsInOpponent };
